import React, { useState, useEffect } from "react";
import { FaTrash, FaSignOutAlt, FaUserShield, FaUpload, FaSpinner, FaFileImage, FaCheckCircle, FaExclamationTriangle, FaPlus, FaSync } from "react-icons/fa";
import { auth, logoutUser, isUserAdmin, onAuthChange } from "../../firebase";
import { apiRequest } from "../../services/api";
import "./AdminPanel.css";

const AdminPanel = ({ onLogout }) => {
  const [status, setStatus] = useState({ type: "", message: "" });
  const [adminUser, setAdminUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ ogloszenia: false, intencje: false, urszula: false });
  const [previewImage, setPreviewImage] = useState(null);
  const [ogloszeniaFiles, setOgloszeniaFiles] = useState([]);
  const [intencjeFiles, setIntencjeFiles] = useState([]);
  const [urszulaFiles, setUrszulaFiles] = useState([]);

  // Pobieranie listy plików z backendu
  const fetchFilesFromBackend = async (category) => {
    try {
      const response = await apiRequest(`/upload/files/${category}`, {
        method: 'GET',
      });

      if (response.success) {
        if (category === 'ogloszenia') {
          setOgloszeniaFiles(response.files);
        } else if (category === 'intencje') {
          setIntencjeFiles(response.files);
        } else if (category === 'urszula') {
          setUrszulaFiles(response.files);
        }
      }
    } catch (error) {
      console.error(`Błąd pobierania ${category}:`, error);
    }
  };

  // Załaduj pliki przy starcie
  useEffect(() => {
    if (isAdmin) {
      fetchFilesFromBackend('ogloszenia');
      fetchFilesFromBackend('intencje');
      fetchFilesFromBackend('urszula');
    }
  }, [isAdmin]);

  // Sprawdzenie autoryzacji
  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      if (user) {
        setAdminUser(user);
        const adminStatus = isUserAdmin(user);
        setIsAdmin(adminStatus);

        if (!adminStatus) {
          setStatus({ type: "error", message: "Brak uprawnień administratora" });
          setTimeout(() => {
            onLogout();
          }, 2000);
        }
      } else {
        onLogout();
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [onLogout]);

  const handleLogout = async () => {
    const result = await logoutUser();
    if (result.success) {
      onLogout();
    } else {
      setStatus({ type: "error", message: "Błąd podczas wylogowywania" });
    }
  };

  // Dodawanie pliku dla Urszuli
  const handleAddUrszulaFile = async () => {
    // Tworzymy ukryty input file
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg, image/png, image/webp, image/gif';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Walidacja typu pliku
      if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
        setStatus({ type: "error", message: "Dozwolone formaty: JPG, PNG, WEBP, GIF" });
        return;
      }

      // Walidacja rozmiaru (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setStatus({ type: "error", message: "Plik nie może przekraczać 5MB" });
        return;
      }

      setUploading(prev => ({ ...prev, urszula: true }));
      setStatus({ type: "info", message: `Dodawanie ${file.name}...` });

      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await apiRequest(`/upload/urszula`, {
          method: 'POST',
          body: formData,
        });

        if (response.success) {
          await fetchFilesFromBackend('urszula');
          setStatus({ type: "success", message: `Plik ${file.name} został dodany!` });
          setTimeout(() => {
            setStatus({ type: "", message: "" });
          }, 3000);
        } else {
          setStatus({ type: "error", message: response.error || "Błąd dodawania pliku" });
        }
      } catch (error) {
        console.error('Upload error:', error);
        setStatus({ type: "error", message: "Błąd dodawania pliku" });
      } finally {
        setUploading(prev => ({ ...prev, urszula: false }));
      }
    };
    
    input.click();
  };

  // Aktualizacja pliku (podmiana ostatniego)
  const handleUpdateUrszulaFile = async () => {
    if (urszulaFiles.length === 0) {
      setStatus({ type: "error", message: "Brak pliku do aktualizacji" });
      return;
    }

    // Tworzymy ukryty input file
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg, image/png, image/webp, image/gif';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Walidacja typu pliku
      if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
        setStatus({ type: "error", message: "Dozwolone formaty: JPG, PNG, WEBP, GIF" });
        return;
      }

      // Walidacja rozmiaru (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setStatus({ type: "error", message: "Plik nie może przekraczać 5MB" });
        return;
      }

      setUploading(prev => ({ ...prev, urszula: true }));
      setStatus({ type: "info", message: `Aktualizowanie ${file.name}...` });

      const formData = new FormData();
      formData.append('image', file);
      // Przesyłamy ID ostatniego pliku do zastąpienia
      const lastFile = urszulaFiles[urszulaFiles.length - 1];
      formData.append('oldFileId', lastFile.id);

      try {
        const response = await apiRequest(`/upload/urszula/update`, {
          method: 'PUT',
          body: formData,
        });

        if (response.success) {
          await fetchFilesFromBackend('urszula');
          setStatus({ type: "success", message: `Plik ${file.name} zastąpił poprzedni!` });
          setTimeout(() => {
            setStatus({ type: "", message: "" });
          }, 3000);
        } else {
          setStatus({ type: "error", message: response.error || "Błąd aktualizacji pliku" });
        }
      } catch (error) {
        console.error('Update error:', error);
        setStatus({ type: "error", message: "Błąd aktualizacji pliku" });
      } finally {
        setUploading(prev => ({ ...prev, urszula: false }));
      }
    };
    
    input.click();
  };

  const handleFileUpload = async (file, category) => {
    if (!file) return;

    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      setStatus({ type: "error", message: "Dozwolone formaty: JPG, PNG, WEBP, GIF" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setStatus({ type: "error", message: "Plik nie może przekraczać 5MB" });
      return;
    }

    setUploading(prev => ({ ...prev, [category]: true }));
    setStatus({ type: "info", message: `Przesyłanie ${file.name}...` });

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await apiRequest(`/upload/${category}`, {
        method: 'POST',
        body: formData,
      });

      if (response.success) {
        await fetchFilesFromBackend(category);
        setStatus({ type: "success", message: `Plik ${file.name} został przesłany!` });
        setTimeout(() => {
          setStatus({ type: "", message: "" });
        }, 3000);
      } else {
        setStatus({ type: "error", message: response.error || "Błąd przesyłania pliku" });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setStatus({ type: "error", message: "Błąd przesyłania pliku" });
    }

    setUploading(prev => ({ ...prev, [category]: false }));
  };

  const handleDeleteFile = async (file, category) => {
    if (window.confirm(`Czy na pewno chcesz usunąć ${file.name}?`)) {
      try {
        const publicId = encodeURIComponent(file.publicId);
        const response = await apiRequest(`/upload/files/${publicId}`, {
          method: 'DELETE',
        });

        if (response.success) {
          await fetchFilesFromBackend(category);
          setStatus({ type: "success", message: `Plik ${file.name} został usunięty` });
          setTimeout(() => {
            setStatus({ type: "", message: "" });
          }, 2000);
        } else {
          setStatus({ type: "error", message: response.error || "Błąd usuwania" });
        }
      } catch (error) {
        console.error('Delete error:', error);
        setStatus({ type: "error", message: "Błąd usuwania pliku" });
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const FileList = ({ files, category, onDelete }) => {
    if (files.length === 0) {
      return (
        <div className="file-list-empty">
          <p>📂 Brak plików</p>
          <small>Dodaj plik za pomocą przycisku powyżej</small>
        </div>
      );
    }

    return (
      <ul className="file-list-simple">
        {files.map((file) => (
          <li key={file.id} className="file-item-simple">
            <span
              className="file-name-simple"
              onClick={() => setPreviewImage(file.url)}
            >
              📄 {file.name}
            </span>
            <span className="file-date-simple">
              {formatDate(file.createdAt)}
            </span>
            <button
              className="delete-btn-simple"
              onClick={() => onDelete(file, category)}
            >
              <FaTrash />
            </button>
          </li>
        ))}
      </ul>
    );
  };

  const UploadArea = ({ category, title, files, onUpload, isUploading, showDragDrop = true }) => {
    const handleDrop = (e) => {
      if (!showDragDrop) return;
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) onUpload(file, category);
    };

    const handleSelect = (e) => {
      const file = e.target.files[0];
      if (file) onUpload(file, category);
    };

    return (
      <div className="admin-box">
        <h2>{title}</h2>

        {showDragDrop && (
          <div
            className="upload-area"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <label className="upload-label">
              <input
                type="file"
                accept="image/jpeg, image/png, image/webp, image/gif"
                onChange={handleSelect}
                disabled={isUploading}
              />
              <div className="upload-content">
                {isUploading ? (
                  <>
                    <FaSpinner className="spinner" />
                    <p>Przesyłanie...</p>
                  </>
                ) : (
                  <>
                    <FaUpload className="upload-icon" />
                    <p>Kliknij lub przeciągnij plik</p>
                    <small>JPG, PNG, WEBP, GIF (max 5MB)</small>
                  </>
                )}
              </div>
            </label>
          </div>
        )}

        <FileList files={files} category={category} onDelete={handleDeleteFile} />
      </div>
    );
  };

  // Komponent dla Mszy Św. Urszuli z przyciskami Dodaj i Aktualizuj
  const UrszulaManager = () => {
    return (
      <div className="admin-box urszula-box">
        <h2>Msza Św. Urszuli</h2>
        
        <div className="urszula-buttons">
          <button
            className="urszula-btn urszula-add-btn"
            onClick={handleAddUrszulaFile}
            disabled={uploading.urszula}
          >
            {uploading.urszula ? <FaSpinner className="spinner" /> : <FaPlus />}
            <span>Dodaj</span>
          </button>
          
          <button
            className="urszula-btn urszula-update-btn"
            onClick={handleUpdateUrszulaFile}
            disabled={uploading.urszula || urszulaFiles.length === 0}
          >
            {uploading.urszula ? <FaSpinner className="spinner" /> : <FaSync />}
            <span>Aktualizuj</span>
          </button>
        </div>

        <FileList files={urszulaFiles} category="urszula" onDelete={handleDeleteFile} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <FaSpinner className="spinner" />
          <p>Sprawdzanie uprawnień...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <FaUserShield />
          <h2>Brak dostępu</h2>
          <p>Nie masz uprawnień do panelu administratora</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header-bar">
        <h1 className="admin-header">Panel Administratora</h1>
        <div className="admin-user-info">
          <span className="admin-email">{adminUser?.email}</span>
          <button className="logout-btn" onClick={handleLogout}>
            <FaSignOutAlt className="logout-icon" />
            <span>Wyloguj</span>
          </button>
        </div>
      </div>

      {status.message && (
        <div className={`status-message ${status.type}`}>
          {status.type === "success" && <FaCheckCircle />}
          {status.type === "error" && <FaExclamationTriangle />}
          {status.type === "info" && <FaSpinner className="spinner" />}
          <span>{status.message}</span>
        </div>
      )}

      <div className="admin-grid">
        <UploadArea
          category="ogloszenia"
          title="Ogłoszenia parafialne"
          files={ogloszeniaFiles}
          onUpload={handleFileUpload}
          isUploading={uploading.ogloszenia}
          showDragDrop={true}
        />

        <UploadArea
          category="intencje"
          title="Intencje mszalne"
          files={intencjeFiles}
          onUpload={handleFileUpload}
          isUploading={uploading.intencje}
          showDragDrop={true}
        />

        <UrszulaManager />
      </div>

      {/* Modal podglądu zdjęcia */}
      {previewImage && (
        <div className="modal" onClick={() => setPreviewImage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={previewImage} alt="preview" className="modal-image" />
            <button className="modal-close" onClick={() => setPreviewImage(null)}>
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;